import React from "react";
import { useTranslation } from "react-i18next";
import { Plus, Edit, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { weekDays } from "./scheduleManagerConstants";

const ScheduleManagerLayout = ({
  displaySchedules,
  selectedScheduleId,
  setSelectedScheduleId,
  selectedSchedule,
  handleEditSchedule,
  handleOpenDeleteDialog,
  handleOpenSlotModal,
  handleDeleteSlot,
}) => {
  const { t } = useTranslation();
  const getDayLabel = (dayValue) => {
    const day = weekDays.find((d) => d.value === dayValue?.toUpperCase());
    return day ? t(day.labelKey) : dayValue;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-base">{t("planning.scheduleManager.layout.listTitle")}</CardTitle>
        <CardDescription>{t("planning.scheduleManager.layout.listDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {displaySchedules.map((schedule) => (
          <button
            key={schedule.id}
            onClick={() => setSelectedScheduleId(schedule.id)}
            className={`w-full text-left p-3 rounded border transition ${
              selectedScheduleId === schedule.id
                ? "border-primary bg-primary/10"
                : "border-border hover:border-border"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{schedule.name}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{schedule.description}</p>
              </div>
              <Badge variant="outline" className="text-xs flex-shrink-0">
                {t("planning.scheduleManager.layout.slotCount", { count: schedule.total_slots })}
              </Badge>
            </div>
          </button>
        ))}
      </CardContent>
    </Card>

    <Card className="h-full">
      <CardHeader className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">{t("planning.scheduleManager.layout.detailTitle")}</CardTitle>
            <CardDescription>{t("planning.scheduleManager.layout.detailDescription")}</CardDescription>
          </div>
          {selectedSchedule && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleEditSchedule}>
                <Edit className="h-4 w-4 mr-1" /> {t("planning.scheduleManager.actions.edit")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-500 border-red-900/40"
                onClick={handleOpenDeleteDialog}
              >
                <Trash2 className="h-4 w-4 mr-1" /> {t("planning.scheduleManager.actions.delete")}
              </Button>
            </div>
          )}
        </div>

        {selectedSchedule && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline">{t("planning.scheduleManager.layout.type")}: {selectedSchedule.schedule_type}</Badge>
            <Badge variant="outline">{t("planning.scheduleManager.layout.cycle")}: {selectedSchedule.cycle_type}</Badge>
            <Badge variant="outline">{t("planning.scheduleManager.layout.lateTolerance")}: {selectedSchedule.late_arrival_tolerance_minutes} min</Badge>
            <Badge variant="outline">{t("planning.scheduleManager.layout.earlyTolerance")}: {selectedSchedule.early_departure_tolerance_minutes} min</Badge>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {!selectedSchedule && (
          <p className="text-center text-sm text-muted-foreground py-12">
            {t("planning.scheduleManager.layout.selectForDetails")}
          </p>
        )}

        {selectedSchedule && (
          <>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">{t("planning.scheduleManager.layout.timeSlots")}</h4>
              <Button size="sm" variant="outline" onClick={() => handleOpenSlotModal()}>
                <Plus className="h-3 w-3 mr-1" /> {t("planning.scheduleManager.actions.addSlot")}
              </Button>
            </div>

            {selectedSchedule.time_slots?.length ? (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("planning.scheduleManager.layout.day")}</TableHead>
                      <TableHead>{t("planning.scheduleManager.layout.start")}</TableHead>
                      <TableHead>{t("planning.scheduleManager.layout.end")}</TableHead>
                      <TableHead>{t("planning.scheduleManager.layout.break")}</TableHead>
                      <TableHead className="w-24">{t("planning.scheduleManager.layout.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedSchedule.time_slots
                      .slice()
                      .sort(
                        (a, b) =>
                          weekDays.findIndex((d) => d.value === a.week_day) -
                          weekDays.findIndex((d) => d.value === b.week_day)
                      )
                      .map((slot) => (
                        <TableRow key={slot.id}>
                          <TableCell className="font-medium">
                            {getDayLabel(slot.week_day)}
                          </TableCell>
                          <TableCell>{slot.start_time?.substring(0, 5)}</TableCell>
                          <TableCell>{slot.end_time?.substring(0, 5)}</TableCell>
                          <TableCell>
                            {slot.break_start && slot.break_end
                              ? `${slot.break_start.substring(0, 5)} - ${slot.break_end.substring(0, 5)}`
                              : "-"}
                          </TableCell>
                          <TableCell className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenSlotModal(slot)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500"
                              onClick={() => handleDeleteSlot(slot.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-sm text-muted-foreground py-12 border rounded-lg">
                {t("planning.scheduleManager.layout.noSlots")}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
    </div>
  );
};

export default ScheduleManagerLayout;
